import {
  CopyToClipboardButton,
  DetailsRow,
  ScrollablePane,
  ScrollbarVisibility,
  Stack
} from "@bps/fluent-ui";
import { DateTimeWithDurationSinceTooltip } from "@components/DateTimeWithDurationSinceTooltip";
import { TableGuidDisplay } from "@components/TableGuidDisplay";
import { DataTable, DataTableColumn } from "@components/tables/DataTable";
import { FieldDeviceProperty } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useFieldDeviceProperties } from "@libs/api/gateways/field/field-ops-gateway.hooks";

import { PropertiesFilterValues } from "./PropertiesFilter";

interface PropertiesProps {
  deviceId: string;
  filter: PropertiesFilterValues;
}

export const PropertiesTable = ({ deviceId, filter }: PropertiesProps) => {
  const propertiesQuery = useFieldDeviceProperties(deviceId);
  const { data, isLoading, error } = propertiesQuery;
  const errorMessage = error?.message;

  const columns: DataTableColumn<FieldDeviceProperty>[] = [
    {
      key: "propertyType",
      name: "Name",
      minWidth: 150,
      maxWidth: 300,
      isResizable: true,
      isSorted: true,
      sort: true,
      onRender: (property: FieldDeviceProperty) => (
        <CopyToClipboardButton
          clipboardContent={property.propertyType}
          text={property.propertyType}
        />
      )
    },
    {
      key: "propertyValue",
      name: "Value",
      minWidth: 150,
      isResizable: true,
      onRender: (property: FieldDeviceProperty) =>
        property.propertyValue && (
          <CopyToClipboardButton
            clipboardContent={property.propertyValue}
            text={property.propertyValue}
          />
        )
    },
    {
      key: "lastObservedUtc",
      name: "Last Observed",
      minWidth: 150,
      onRender: (property: FieldDeviceProperty) => (
        <DateTimeWithDurationSinceTooltip dateUtc={property.lastObservedUtc} />
      )
    },
    {
      key: "lastSourceId",
      name: "Last Source ID",
      minWidth: 150,
      onRender: (property: FieldDeviceProperty) => (
        <TableGuidDisplay id={property.lastSourceId} />
      )
    },
    {
      key: "lastSourceType",
      name: "Last Source Type",
      onRender: (property: FieldDeviceProperty) => property.lastSourceType
    }
  ];

  return (
    <Stack grow styles={{ root: { position: "relative" } }} verticalFill>
      <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
        <DataTable
          stickyHeader
          isSelectedOnFocus
          columns={columns}
          onRenderRow={row => (row ? <DetailsRow {...row} /> : null)}
          enableShimmer={isLoading}
          errorMessage={errorMessage}
          items={filterAndSortProperties(data, filter)}
        />
      </ScrollablePane>
    </Stack>
  );
};

const filterAndSortProperties = (
  properties: FieldDeviceProperty[] | undefined,
  filter: PropertiesFilterValues
): FieldDeviceProperty[] => {
  if (!properties) return [];

  return properties
    .filter(
      d =>
        filter.name === "" ||
        d.propertyType.toLowerCase().indexOf(filter.name.toLowerCase()) >= 0 ||
        (d.propertyValue &&
          d.propertyValue.toLowerCase().indexOf(filter.name.toLowerCase()) >= 0)
    )
    .sort((a: FieldDeviceProperty, b: FieldDeviceProperty) => {
      return a.propertyType < b.propertyType ? 1 : -1;
    });
};
